/** What a design file's state is read from. */
export interface DesignFileFacts {
	isActive: boolean
	createdAt: Date | string
}

/** An order's design files by state. Each list keeps the input order. */
export interface DesignFileStates<T> {
	drafts: T[]
	active: T[]
	inactive: T[]
}

/**
 * Split an order's design files into drafts, active and inactive.
 *
 * Only `isActive` is stored: an inactive file is a draft when it was created after every active
 * file (or there is none), and a replaced file otherwise. That holds because files are uploaded
 * only while the order is in progress, and a submit activates every draft at once.
 */
export function designFileStates<T extends DesignFileFacts>(files: readonly T[]): DesignFileStates<T> {
	const time = (file: T) => new Date(file.createdAt).getTime()
	const active = files.filter((file) => file.isActive)
	const newestActive = Math.max(Number.NEGATIVE_INFINITY, ...active.map(time))
	const inactive = files.filter((file) => !file.isActive)
	return {
		drafts: inactive.filter((file) => time(file) > newestActive),
		active,
		inactive: inactive.filter((file) => time(file) <= newestActive),
	}
}
